import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import React, { useState, useEffect } from 'react';
import { Eye, LayoutGrid, CheckSquare, PlusCircle, Settings } from 'lucide-react';

import SELLER_TEMPLATE from './templates/sellerTemplate';
import BUYER_TEMPLATE from './templates/buyerTemplate';

// Firestore service (replaces localStorage)
import {
  getUserProfile,
  loadOrgData as loadOrgDataFromFirestore,
  createProject as fsCreateProject,
  createTasks as fsCreateTasks,
  getTasks as fsGetTasks,
  updateProject as fsUpdateProject,
  deleteProject as fsDeleteProject,
  updateTask as fsUpdateTask,
  addMemberByEmail as fsAddMember,
  updateMember as fsUpdateMember,
  deleteMember as fsDeleteMember,
  saveDefaultAssignments as fsSaveDefaultAssignments,
} from './services/firestoreService';
import { isSuperAdminEmail } from './services/superAdminConfig';

import LoginScreen from './components/auth/LoginScreen';
import Sidebar from './components/layout/Sidebar';
import DashboardView from './components/dashboard/DashboardView';
import ArchiveView from './components/dashboard/ArchiveView';
import SalesPipeline from './components/dashboard/SalesPipeline';
import SettingsPage, { BillingTab } from './components/dashboard/SettingsPage';
import ProjectView from './components/dashboard/ProjectView';
import NewProjectModal from './components/tasks/NewProjectModal';
import EditProfileModal from './components/tasks/EditProfileModal';
import SuperAdminDashboard from './components/superadmin/SuperAdminDashboard';
import MyProfilePage from './components/dashboard/MyProfilePage';
import SupportPage from './components/support/SupportPage';
import { loadOrgDataForImpersonation } from './services/superAdminService';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import OrgSelector from './components/auth/OrgSelector';
import { getOrganization, migrateUserToMultiOrg, setActiveOrg, getCollaboratingDeals } from './services/firestoreService';
import { sendNewDealNotifications, sendNotification } from './services/notificationService';
import { logLogin, logDealCreated, logTaskCompleted, logDueDateSet, logSeatAdded, logDealArchived, logTaskCustomized } from './services/activityLogService';
import { isMobileNative } from './utils/platform';

const App = () => {
  // ── Native-only: tag <html> so safe-area CSS in index.css scopes correctly
  useEffect(() => {
    if (isMobileNative) {
      document.documentElement.classList.add('cw-native');
      return () => document.documentElement.classList.remove('cw-native');
    }
  }, []);

  const [user, setUser] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentView, setCurrentView] = useState(() => sessionStorage.getItem('currentView') || 'pipeline');
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = sessionStorage.getItem('selectedProject');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dealToDelete, setDealToDelete] = useState(null);
  const [defaultAssignments, setDefaultAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [editProfileMember, setEditProfileMember] = useState(null);
  const [editProfileMode, setEditProfileMode] = useState('self');
  const [showMyTasksModal, setShowMyTasksModal] = useState(false);
  const [myTasksModalDeal, setMyTasksModalDeal] = useState(null);
  const [myTasksModalType, setMyTasksModalType] = useState('assigned');
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [taskDetailTask, setTaskDetailTask] = useState(null);
  const [taskDetailProject, setTaskDetailProject] = useState(null);

  useEffect(() => {
    sessionStorage.setItem('currentView', currentView);
  }, [currentView]);

  useEffect(() => {
    if (selectedProject) {
      sessionStorage.setItem('selectedProject', JSON.stringify(selectedProject));
    } else {
      sessionStorage.removeItem('selectedProject');
    }
  }, [selectedProject]);

  // Multi-org state
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [userOrgIds, setUserOrgIds] = useState([]);
  const [collaboratingOrgs, setCollaboratingOrgs] = useState([]);
  const [isCollaboratingOrg, setIsCollaboratingOrg] = useState(false);

  // Trial state
  const [orgPlan, setOrgPlan] = useState('trial');
const [orgName, setOrgName] = useState('');
  const [orgCreatedAt, setOrgCreatedAt] = useState(null);
  const [orgTrialExtendedUntil, setOrgTrialExtendedUntil] = useState(null);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStepState] = useState(0);
  const [showBillingFromPaywall, setShowBillingFromPaywall] = useState(false);

  // Impersonation state
  const [impersonation, setImpersonation] = useState(null);
  const [originalOrgId, setOriginalOrgId] = useState(null);
  const [originalProjects, setOriginalProjects] = useState(null);
  const [originalTeamMembers, setOriginalTeamMembers] = useState(null);
  const [originalDefaultAssignments, setOriginalDefaultAssignments] = useState(null);

  // ============================================
  // LOAD ORG DATA (shared by auth listener + manual login)
  // ============================================
  const checkOnboarding = async (targetOrgId) => {
    try {
      const orgDoc = await getOrganization(targetOrgId);
      if (orgDoc && !orgDoc.onboardingComplete) {
        setShowOnboarding(true);
        setOnboardingStepState(orgDoc.onboardingStep || 0);
      } else {
        setShowOnboarding(false);
      }
    } catch (err) {
      console.error('Error checking onboarding status:', err);
    }
  };

  const initializeOrg = async (userData) => {
    try {
      const orgData = await loadOrgDataFromFirestore(userData.orgId);
      setTeamMembers(orgData.members || []);
      const projectsWithTasks = (orgData.projects || []).map(p => ({
        ...p,
        status: p.status || 'active',
      }));
      setProjects(projectsWithTasks);
      setDefaultAssignments(orgData.defaultAssignments || {});
      const orgDoc = await getOrganization(userData.orgId);
      if (orgDoc) {
        setOrgPlan(orgDoc.plan || 'trial');
setOrgName(orgDoc.name || '');
        setOrgCreatedAt(orgDoc.createdAt || null);
        setOrgTrialExtendedUntil(orgDoc.trialExtendedUntil || null);
      }
    } catch (error) {
      console.error('Error loading org data:', error);
    }
  };

  // ============================================
  // AUTH STATE LISTENER (auto-login on page refresh)
  // ============================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && firebaseUser.emailVerified) {
        try {
          const profile = await getUserProfile(firebaseUser.uid);
          if (profile && profile.orgId) {
            // Migrate existing user to multi-org if needed
            if (!profile.orgIds) {
              await migrateUserToMultiOrg(firebaseUser.uid, profile.orgId);
              profile.orgIds = [profile.orgId];
              profile.defaultOrgId = profile.orgId;
            }

            const userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              firstName: profile.firstName || '',
              lastName: profile.lastName || '',
              orgId: profile.defaultOrgId || profile.orgId,
              platformRole: profile.platformRole || 'user',
            };

            // Load collaborating orgs
            if (profile.collaboratingOrgs?.length) {
              setCollaboratingOrgs(profile.collaboratingOrgs);
            }

            // If multiple orgs, show selector
            if (profile.orgIds.length > 1) {
              setUser(userData);
              setUserOrgIds(profile.orgIds);
              setShowOrgSelector(true);
            } else {
              setUser(userData);
              setOrgId(userData.orgId);
              await initializeOrg(userData);

              await checkOnboarding(userData.orgId);
            }
          } else if (profile) {
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              firstName: profile.firstName || '',
              lastName: profile.lastName || '',
              orgId: null,
              platformRole: profile.platformRole || 'user',
            });
            setShowOnboarding(true);
          }
        } catch (error) {
          console.error('Error restoring session:', error);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  

  // ============================================
  // LOGIN CALLBACK (from LoginScreen)
  // ============================================
  const handleLogin = async (userData) => {
    // Fetch fresh profile to check for multi-org
    const profile = await getUserProfile(userData.uid);

    // Migrate existing user to multi-org if needed
    if (profile && profile.orgId && !profile.orgIds) {
      await migrateUserToMultiOrg(userData.uid, profile.orgId);
      profile.orgIds = [profile.orgId];
      profile.defaultOrgId = profile.orgId;
    }

    const orgIds = profile?.orgIds || (userData.orgId ? [userData.orgId] : []);

    if (orgIds.length > 1) {
      setUser(userData);
      setUserOrgIds(orgIds);
      setShowOrgSelector(true);
      setLoading(false);
      return;
    }

    setUser(userData);
    setOrgId(userData.orgId);
    if (userData.orgId) {
      setLoading(true);
      await initializeOrg(userData);
      logLogin(userData.orgId, userData.uid);

      await checkOnboarding(userData.orgId);

      setLoading(false);
    } else {
      setShowOnboarding(true);
      setLoading(false);
    }

  };

  // ============================================
  // ORG SELECTION & SWITCHING
  // ============================================
  const handleSelectOrg = async (selectedOrgId) => {
    setShowOrgSelector(false);
    setOrgId(selectedOrgId);
    setLoading(true);

    const userData = { ...user, orgId: selectedOrgId };
    setUser(userData);
    await setActiveOrg(user.uid, selectedOrgId);
    await initializeOrg(userData);
    logLogin(selectedOrgId, user.uid);

    await checkOnboarding(selectedOrgId);

    setCurrentView('pipeline');
    setSelectedProject(null);
    setSelectedTask(null);
    setLoading(false);
  };

  const handleSwitchOrg = async (newOrgId) => {
    if (newOrgId === orgId) return;
    setLoading(true);
    setOrgId(newOrgId);

    // Check if this is a collaborating org (not a home org)
    const isCollab = !user.orgIds?.includes(newOrgId) ||
      collaboratingOrgs.some(o => o.orgId === newOrgId);

    setIsCollaboratingOrg(isCollab);

    if (isCollab) {
      // Only load the specific deals this user is a collaborator on
      try {
        const collabDeals = await getCollaboratingDeals(newOrgId, user.uid);
        const dealsWithTasks = await Promise.all(
          collabDeals.map(async (deal) => {
            const tasks = await fsGetTasks(newOrgId, deal.id);
            return { ...deal, tasks, status: deal.status || 'active' };
          })
        );
        setProjects(dealsWithTasks);
        setTeamMembers([]);
        setDefaultAssignments({});
        setUser(prev => ({ ...prev, orgId: newOrgId }));
      } catch (err) {
        console.error('Error loading collaborating org:', err);
      }
    } else {
      const userData = { ...user, orgId: newOrgId };
      setUser(userData);
      await setActiveOrg(user.uid, newOrgId);
      await initializeOrg(userData);

      await checkOnboarding(newOrgId);
    }

    setCurrentView('dashboard');
    setSelectedProject(null);
    setSelectedTask(null);
    setImpersonation(null);
    setLoading(false);
  };

  // ============================================
  // PROJECT CRUD
  // ============================================
  const reloadProjectTasks = async (projectId) => {
    const updatedTasks = await fsGetTasks(orgId, projectId);
    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, tasks: updatedTasks } : p
    ));
    setSelectedProject(prev => prev?.id === projectId ? { ...prev, tasks: updatedTasks } : prev);
  };

  const createProject = async (projectData) => {
    if (!isAdmin()) {
      alert('Permission denied: Only Admins can create new deals.');
      return;
    }
    const template = projectData.type === 'Buyer' ? BUYER_TEMPLATE : SELLER_TEMPLATE;

    // Prepare project doc (no tasks — those go in subcollection)
    const projectDoc = {
      type: projectData.type,
      clientName: projectData.clientName || '',
      propertyAddress: projectData.propertyAddress || '',
      phone: projectData.phone || '',
      email: projectData.email || '',
      primaryAgent: projectData.primaryAgent || '',
      contractDate: projectData.contractDate || '',
      status: 'active',
      listPrice: projectData.listPrice || '',
      contractPrice: projectData.contractPrice || '',
      commissionPercent: projectData.commissionPercent || '',
      brokerSplitType: projectData.brokerSplitType || '',
      brokerSplitValue: projectData.brokerSplitValue || '',
      transactionFee: projectData.transactionFee || '',
      tcFee: projectData.tcFee || '',
      ucDate: projectData.ucDate || '',
      listingDate: projectData.listingDate || '',
      closingDate: projectData.closingDate || '',
    };

    // 1. Create project in Firestore
    const projectId = await fsCreateProject(orgId, projectDoc);

    // 2. Build tasks from template
    const tasks = template.flatMap(themeGroup =>
      themeGroup.activities.map((activity, idx) => {
        const taskType = projectData.type === 'Buyer' ? 'buyer' : 'seller';
        const taskKey = `${taskType}-${themeGroup.theme}-${activity.name}`;
        const defaultAssigneeId = defaultAssignments[taskKey];
        const primaryAgentMember = teamMembers.find(m =>
          `${m.firstName} ${m.lastName}`.trim() === projectData.primaryAgent
        );
        const soloMember = teamMembers.length === 1 ? teamMembers[0] : null;
        const currentUserMember = teamMembers.find(m => m.email === user?.email) || null;
        const defaultAssignee = defaultAssigneeId
          ? teamMembers.find(m => m.id === defaultAssigneeId)
          : soloMember || primaryAgentMember || currentUserMember || null;
        return {
          ...activity,
          theme: themeGroup.theme,
          name: activity.name,
          notes: activity.notes,
          status: 'not-started',
          dueDate:
            projectData.contractDate && activity.daysOffset
              ? calculateDueDate(projectData.contractDate, activity.daysOffset)
              : null,
          userNotes: '',
          assignedTo: defaultAssignee
            ? `${defaultAssignee.firstName} ${defaultAssignee.lastName}`
            : '',
        };
      })
    );

    // 3. Batch write tasks to Firestore subcollection
    await fsCreateTasks(orgId, projectId, tasks);

    // 4. Load tasks back to get their Firestore IDs
    const loadedTasks = await fsGetTasks(orgId, projectId);

    const newProject = { id: projectId, ...projectDoc, tasks: loadedTasks, orgId };

    // Log deal created for KPI tracking
    logDealCreated(orgId, user.uid, { projectId, type: projectDoc.type, clientName: projectDoc.clientName, propertyAddress: projectDoc.propertyAddress });

    // Send new deal email notifications to all assigned team members
    sendNewDealNotifications(projectDoc, loadedTasks, teamMembers);

    setProjects(prev => [...prev, newProject]);
    setShowNewProjectModal(false);
    setSelectedProject(newProject);
    setCurrentView('project');
  };

  const updateProject = async (projectId, updates) => {
    // Separate tasks from project-level updates (tasks live in subcollection)
    const { tasks, id, ...projectUpdates } = updates;

    if (Object.keys(projectUpdates).length > 0) {
      await fsUpdateProject(orgId, projectId, projectUpdates);
    }

    const updated = projects.map(p =>
      p.id === projectId ? { ...p, ...updates } : p
    );
    setProjects(updated);
    if (selectedProject?.id === projectId) {
      setSelectedProject({ ...selectedProject, ...updates });
    }
  };

  const archiveProject = async () => {
    await updateProject(selectedProject.id, { status: 'archived' });
    logDealArchived(orgId, user.uid, { projectId: selectedProject.id, clientName: selectedProject.clientName || '' });
    setShowArchiveModal(false);
    setSelectedProject(null);
    setCurrentView('dashboard');
  };

  const unarchiveProject = async (projectId) => {
    await updateProject(projectId, { status: 'active' });
  };

  const deleteProject = async (projectId = null) => {
    const idToDelete = projectId || selectedProject?.id;
    if (!idToDelete) return;

    await fsDeleteProject(orgId, idToDelete);
    setProjects(prev => prev.filter(p => p.id !== idToDelete));

    setShowDeleteModal(false);
    setDealToDelete(null);
    if (selectedProject?.id === idToDelete) {
      setSelectedProject(null);
      setCurrentView('dashboard');
    }
  };

  // ============================================
  // TASK CRUD
  // ============================================
  const updateTasksBatch = async (projectId, taskUpdates) => {
    await Promise.all(taskUpdates.map(({ taskId, updates }) =>
      fsUpdateTask(orgId, projectId, taskId, updates)
    ));
    const updated = projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        tasks: p.tasks.map(t => {
          const match = taskUpdates.find(u => u.taskId === t.id);
          return match ? { ...t, ...match.updates } : t;
        }),
      };
    });
    setProjects(updated);
    if (selectedProject?.id === projectId) {
      setSelectedProject(updated.find(p => p.id === projectId));
    }
  };

  const updateTask = async (projectId, taskId, updates) => {
    await fsUpdateTask(orgId, projectId, taskId, updates);

    // Log task completed for KPI tracking
    if (updates.status === 'complete') {
      const project = projects.find(p => p.id === projectId);
      const task = project?.tasks?.find(t => t.id === taskId);
      logTaskCompleted(orgId, user.uid, { projectId, taskId, taskName: task?.name || '' });
    }

    // Log due date set for KPI tracking
    if (updates.dueDate) {
      const project = projects.find(p => p.id === projectId);
      const task = project?.tasks?.find(t => t.id === taskId);
      logDueDateSet(orgId, user.uid, { projectId, taskId, taskName: task?.name || '', dueDate: updates.dueDate });
    }

    // Log task customized for KPI tracking (user edited notes)
    if (updates.userNotes !== undefined) {
      const project = projects.find(p => p.id === projectId);
      const task = project?.tasks?.find(t => t.id === taskId);
      logTaskCustomized(orgId, user.uid, { projectId, taskId, taskName: task?.name || '', field: 'userNotes' });
    }

    

    const updated = projects.map(p => {
      if (p.id === projectId) {
        return {
          ...p,
          tasks: p.tasks.map(t => (t.id === taskId ? { ...t, ...updates } : t)),
        };
      }
      return p;
    });
    setProjects(updated);
    if (selectedProject?.id === projectId) {
      setSelectedProject(updated.find(p => p.id === projectId));
    }
  };

  // ============================================
  // TEAM MEMBER CRUD
  // ============================================
  const addTeamMember = async (memberData) => {
    const newMember = {
      ...memberData,
      role: memberData.role || 'member',
      notificationPreference: 'email',
    };
    const result = await fsAddMember(orgId, newMember);
    const memberId = result.uid || result.pendingId;
    const memberWithStatus = { ...newMember, id: memberId, status: result.status };
    setTeamMembers(prev => [...prev, memberWithStatus]);
    if (result.status === 'pending') {
      const org = await getOrganization(orgId);
      await sendNotification({
        action: 'inviteMember',
        email: newMember.email,
        firstName: newMember.firstName,
        lastName: newMember.lastName,
        orgName: org?.name || 'Your team',
      });
    }
    logSeatAdded(orgId, user.uid, { memberId, memberName: `${newMember.firstName || ''} ${newMember.lastName || ''}`.trim() });
  };

  const removeTeamMember = async (memberId) => {
    await fsDeleteMember(orgId, memberId);
    setTeamMembers(prev => prev.filter(m => m.id !== memberId));

    // Clean up default assignments for removed member
    const newAssignments = { ...defaultAssignments };
    Object.keys(newAssignments).forEach(taskId => {
      if (newAssignments[taskId] === memberId) delete newAssignments[taskId];
    });
    handleSaveDefaultAssignments(newAssignments);
  };

  const updateTeamMember = async (memberId, updates) => {
    await fsUpdateMember(orgId, memberId, updates);

    const updated = teamMembers.map(m =>
      m.id === memberId ? { ...m, ...updates } : m
    );
    setTeamMembers(updated);

    // If the user updated their own profile, reflect it
    const updatedMember = updated.find(m => m.id === memberId);
    if (user && updatedMember && user.email === updatedMember.email) {
      setUser(prev => ({
        ...prev,
        firstName: updatedMember.firstName,
        lastName: updatedMember.lastName,
      }));
    }
  };

  // ============================================
  // DEFAULT ASSIGNMENTS
  // ============================================
  const handleSaveDefaultAssignments = async (assignments) => {
    setDefaultAssignments(assignments);
    await fsSaveDefaultAssignments(orgId, assignments);
  };

  // ============================================
  // LOGOUT
  // ============================================
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(null);
    setOrgId(null);
    setProjects([]);
    setTeamMembers([]);
    setDefaultAssignments({});
    setCurrentView('pipeline');
    setSelectedProject(null);
    setSelectedTask(null);
    setImpersonation(null);
    setOriginalOrgId(null);
    setOriginalProjects(null);
    setOriginalTeamMembers(null);
    setOriginalDefaultAssignments(null);
  };

  // ============================================
  // IMPERSONATION (SuperAdmin only)
  // ============================================
  const handleImpersonate = async ({ orgId: targetOrgId, orgName, memberName, memberEmail }) => {
    // Save current state so we can restore it later
    setOriginalOrgId(orgId);
    setOriginalProjects(projects);
    setOriginalTeamMembers(teamMembers);
    setOriginalDefaultAssignments(defaultAssignments);

    // Load target org's data
    try {
      const orgData = await loadOrgDataForImpersonation(targetOrgId);
      setOrgId(targetOrgId);
      setProjects(
        (orgData.projects || []).map(p => ({ ...p, status: p.status || 'active' }))
      );
      setTeamMembers(orgData.members || []);
      setDefaultAssignments({});
      setImpersonation({ orgId: targetOrgId, orgName, memberName, memberEmail });
      setCurrentView('pipeline');
      setSelectedProject(null);
      setSelectedTask(null);
    } catch (error) {
      console.error('Error impersonating:', error);
      alert('Failed to load organization data.');
      setOriginalOrgId(null);
      setOriginalProjects(null);
      setOriginalTeamMembers(null);
      setOriginalDefaultAssignments(null);
    }
  };

  const handleExitImpersonation = () => {
    // Restore original state
    setOrgId(originalOrgId);
    setProjects(originalProjects || []);
    setTeamMembers(originalTeamMembers || []);
    setDefaultAssignments(originalDefaultAssignments || {});
    setImpersonation(null);
    setOriginalOrgId(null);
    setOriginalProjects(null);
    setOriginalTeamMembers(null);
    setOriginalDefaultAssignments(null);
    setCurrentView('superAdmin');
    setSelectedProject(null);
    setSelectedTask(null);
  };

  // ============================================
  // HELPERS (unchanged logic)
  // ============================================
  const getDealTitle = (deal) => {
    if (!deal) return '';
    return deal.type === 'Buyer'
      ? deal.clientName || 'New Buyer Deal'
      : deal.propertyAddress || 'New Seller Deal';
  };

  const isAdmin = () => {
    if (!user || teamMembers.length === 0) return true;
    const currentMember = teamMembers.find(m => m.email === user.email);
    return currentMember?.role === 'admin' || teamMembers[0]?.email === user.email;
  };

  const isSuperAdmin = () => user?.platformRole === 'superAdmin' || isSuperAdminEmail(user?.email);

  const getCurrentUserMember = () => {
    const emailToFind = impersonation ? impersonation.memberEmail : user?.email;
    return teamMembers.find(m => m.email === emailToFind);
  };

  const calculateDueDate = (startDate, offset) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + offset);
    return date.toISOString().split('T')[0];
  };

  const isTaskUrgent = (task) => {
    if (task.status === 'complete') return false;
    if (!task.dueDate) return false;
    const diffDays = Math.ceil(
      (new Date(task.dueDate) - new Date()) / (1000 * 60 * 60 * 24)
    );
    return diffDays < 0 || diffDays <= 3;
  };

  const getTaskColor = (task) => {
    if (task.status === 'complete') return 'bg-blue-500 text-white';
    if (task.status === 'not-applicable') return 'bg-gray-400 text-white';
    if (task.status === 'in-progress') return 'bg-green-500 text-white';
    return 'bg-white text-gray-700 border border-gray-300';
  };

  const getDueSoonTasks = () => {
    const active = projects.filter(p => p.status === 'active');
    const allTasks = active.flatMap(p =>
      p.tasks.map(t => ({
        ...t,
        projectName: p.propertyAddress,
        projectId: p.id,
        projectClientName: p.clientName,
        projectType: p.type,
      }))
    );
    return allTasks.filter(t => {
      if (!t.dueDate || t.status === 'complete' || t.status === 'not-applicable')
        return false;
      const diffDays = Math.ceil(
        (new Date(t.dueDate) - new Date()) / (1000 * 60 * 60 * 24)
      );
      return diffDays >= 0 && diffDays <= 3;
    });
  };

  const getOverdueTasks = () => {
    const active = projects.filter(p => p.status === 'active');
    const allTasks = active.flatMap(p =>
      p.tasks.map(t => ({
        ...t,
        projectName: p.propertyAddress,
        projectId: p.id,
        projectClientName: p.clientName,
        projectType: p.type,
      }))
    );
    return allTasks.filter(t => {
      if (!t.dueDate || t.status === 'complete' || t.status === 'not-applicable')
        return false;
      const today = new Date(); today.setHours(0,0,0,0);
      const due = new Date(t.dueDate); due.setHours(0,0,0,0);
      return due < today;
    });
  };

  const getMyTasks = () => {
    const active = projects.filter(p => p.status === 'active');
    const allTasks = active.flatMap(p =>
      p.tasks.map(t => ({
        ...t,
        projectName: p.propertyAddress,
        projectId: p.id,
        projectClientName: p.clientName,
        projectType: p.type,
      }))
    );
    const normalizedUserName = user
      ? `${user.firstName} ${user.lastName || ''}`.trim().toLowerCase()
      : '';
    return allTasks.filter(t => {
      if (!t.assignedTo) return false;
      return (
        t.assignedTo.toLowerCase().trim() === normalizedUserName &&
        t.status !== 'complete' &&
        t.status !== 'not-applicable'
      );
    });
  };

  const groupTasksByDeal = (tasks) => {
    const grouped = {};
    tasks.forEach(task => {
      if (!grouped[task.projectId]) {
        grouped[task.projectId] = {
          projectId: task.projectId,
          projectName: task.projectName,
          projectClientName: task.projectClientName,
          tasks: [],
          taskCount: 0,
        };
      }
      grouped[task.projectId].tasks.push(task);
      grouped[task.projectId].taskCount++;
    });
    return Object.values(grouped);
  };

  const getMyTasksGroupedByDeal = () => groupTasksByDeal(getMyTasks());
  const getOverdueTasksGroupedByDeal = () => groupTasksByDeal(getOverdueTasks());
  const getDueSoonTasksGroupedByDeal = () => groupTasksByDeal(getDueSoonTasks());

  // --- Shared callbacks (unchanged) ---
  const handleSelectProject = (p) => {
    setSelectedProject(p);
    setCurrentView('project');
  };

  const handleOpenMyProfile = () => {
    setCurrentView('myProfile');
  };

  const handleEditTeamMember = (member) => {
    setEditProfileMember(member);
    setEditProfileMode('admin');
    setShowEditProfileModal(true);
  };

  const handleCloseEditProfile = () => {
    setShowEditProfileModal(false);
    setEditProfileMember(null);
  };

  const handleSaveEditProfile = (memberId, updates) => {
    updateTeamMember(memberId, updates);
    setShowEditProfileModal(false);
    setEditProfileMember(null);
  };

  const handleMyTasksDealClick = (deal, type) => {
    setMyTasksModalDeal(deal);
    setMyTasksModalType(type);
    setShowMyTasksModal(true);
  };

  const handleCloseMyTasksModal = () => {
    setShowMyTasksModal(false);
    setMyTasksModalDeal(null);
    setMyTasksModalType('assigned');
  };

  const handleMyTasksTaskClick = (task) => {
    const project = projects
      .filter(p => p.status === 'active')
      .find(p => p.id === task.projectId);
    setTaskDetailTask(task);
    setTaskDetailProject(project);
    setShowTaskDetailModal(true);
  };

  const handleCloseTaskDetail = () => {
    setShowTaskDetailModal(false);
    setTaskDetailTask(null);
    setTaskDetailProject(null);
  };

  const handleTaskDetailUpdate = (updates) => {
    updateTask(taskDetailTask.projectId, taskDetailTask.id, updates);
    if (myTasksModalDeal) {
      const updatedTasks = myTasksModalDeal.tasks.map(t =>
        t.id === taskDetailTask.id ? { ...t, ...updates } : t
      );
      if (updates.status === 'complete') {
        const remaining = updatedTasks.filter(t => t.status !== 'complete');
        if (remaining.length === 0) {
          setShowMyTasksModal(false);
          setMyTasksModalDeal(null);
          setMyTasksModalType('assigned');
        } else {
          setMyTasksModalDeal({
            ...myTasksModalDeal,
            tasks: remaining,
            taskCount: remaining.length,
          });
        }
      } else {
        setMyTasksModalDeal({ ...myTasksModalDeal, tasks: updatedTasks });
      }
    }
    setShowTaskDetailModal(false);
    setTaskDetailTask(null);
    setTaskDetailProject(null);
  };

  const handleDeleteDeal = (project) => {
    setDealToDelete(project);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    deleteProject(dealToDelete.id);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDealToDelete(null);
  };

  // --- Render ---
  if (loading)
    return (
      <div className="flex items-center justify-center h-screen">Loading...</div>
    );
  if (!user) return <LoginScreen onLogin={handleLogin} />;

  // Show org selector for multi-org users
  if (showOrgSelector) {
    return (
      <OrgSelector
        user={user}
        orgIds={userOrgIds}
        onSelectOrg={handleSelectOrg}
      />
    );
  }

  // Show onboarding wizard for new orgs
  if (showOnboarding && !impersonation) {
    return (
      <OnboardingWizard
        orgId={orgId}
        currentUser={user}
        initialStep={onboardingStep}
        onComplete={() => {
          setShowOnboarding(false);
          setCurrentView('pipeline');
        }}
        onCreateDeal={() => {
          setShowOnboarding(false);
          setShowNewProjectModal(true);
          setCurrentView('pipeline');
        }}
      />
    );
  }

  const impersonatedMember = impersonation ? teamMembers.find(m => m.email === impersonation.memberEmail) : null;
  const effectiveName = impersonation
    ? `${impersonatedMember?.firstName || ''} ${impersonatedMember?.lastName || ''}`.trim().toLowerCase()
    : `${user.firstName} ${user.lastName}`.trim().toLowerCase();
  const effectiveIsAdmin = impersonation
    ? impersonatedMember?.role === 'admin'
    : isCollaboratingOrg
    ? false
    : isAdmin();
  const activeProjects = projects
    .filter(p => p.status === 'active' || p.status === 'closed')
    .filter(p => {
      if (effectiveIsAdmin) return true;
      return (p.tasks || []).some(t => t.assignedTo?.trim().toLowerCase() === effectiveName);
    });
  const archivedProjects = projects.filter(p => p.status === 'archived');

  // Shared sidebar props
  const sidebarProps = {
    projects: activeProjects,
    onSelectProject: handleSelectProject,
    onPipeline: () => setCurrentView('pipeline'),
    onDashboard: () => setCurrentView('dashboard'),
    onArchive: () => setCurrentView('archive'),
    onNewProject: () => setShowNewProjectModal(true),
    onTeamSettings: () => setCurrentView('teamSettings'),
    onMyProfile: handleOpenMyProfile,
    onHelpSupport: () => setCurrentView('helpSupport'),
    onSuperAdmin: () => setCurrentView('superAdmin'),
    onLogout: handleLogout,
    currentView,
    getDealTitle,
    selectedProjectId: selectedProject?.id,
    isAdmin: isAdmin(),
    isSuperAdmin: isSuperAdmin(),
    user,
    orgId,
    userOrgIds,
    onSwitchOrg: handleSwitchOrg,
    impersonation,
    collaboratingOrgs,
    isCollaboratingOrg,
  };

  // --- Trial helpers ---
  const trialDaysLeft = (() => {
    if (orgPlan !== 'trial') return null;
    if (orgTrialExtendedUntil) {
      const extendedUntil = orgTrialExtendedUntil?.toDate ? orgTrialExtendedUntil.toDate() : new Date(orgTrialExtendedUntil);
      return Math.max(0, Math.ceil((extendedUntil.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
    }
    if (!orgCreatedAt) return null;
    const created = orgCreatedAt?.toDate ? orgCreatedAt.toDate() : new Date(orgCreatedAt);
    const daysSince = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.ceil(14 - daysSince));
  })();
  
  const showTrialBanner = orgPlan === 'trial' && trialDaysLeft !== null && trialDaysLeft > 0 && !impersonation && !isSuperAdmin();
  const trialExpired = orgPlan === 'trial' && trialDaysLeft !== null && trialDaysLeft <= 0 && !impersonation && !isSuperAdmin();

  // --- Trial Banner (fixed, shows countdown) ---
  const TrialBanner = () => {
    if (!showTrialBanner) return null;
    const bannerTop = impersonation ? 40 : 0;
    const urgent = trialDaysLeft <= 3;
    const warning = trialDaysLeft <= 7;
        const bg = urgent ? '#dc2626' : warning ? '#d97706' : '#516469';
    // On native iOS, offset by the safe-area inset so the banner sits BELOW the notch.
    const topStyle = isMobileNative
      ? { top: `calc(${bannerTop}px + env(safe-area-inset-top, 0px))` }
      : { top: bannerTop };
    return (
      <div style={{
        position: 'fixed', left: 0, right: 0,
        ...topStyle,
        backgroundColor: bg, color: '#fff', zIndex: 9997,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '9px 16px', fontSize: 13, fontWeight: 600, gap: 12, textAlign: 'center',
      }}>
        <span>
          {trialDaysLeft === 1 ? '⚠️ Last day of your free trial!' : `⏳ ${trialDaysLeft} days left in your free trial`}
        </span>
        {isAdmin() && !isMobileNative && (
          <button
            onClick={() => setCurrentView('teamSettings')}
            style={{
              backgroundColor: '#fff', color: bg, border: 'none',
              borderRadius: 6, padding: '3px 12px', fontSize: 12,
              fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>
            Add Billing
          </button>
        )}
      </div>
    );
  };

  // --- Trial Expired Screen (full paywall) ---
  const TrialExpiredScreen = ({ onSetUpBilling }) => (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: '#071D39',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 24,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: 16, padding: 40,
        maxWidth: 440, width: '100%', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔑</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#071D39', marginBottom: 12 }}>
          Your free trial has ended
        </div>
        <p style={{ fontSize: 15, color: '#516469', lineHeight: 1.6, marginBottom: 28 }}>
          {isMobileNative
            ? 'Your trial has expired. Subscribe at app.getclosd.com on a web browser to continue. After subscribing, sign back in here.'
            : (isAdmin()
                ? 'Your free trial of CLOSd has ended — please complete billing information to get back at it!'
                : "Your team's CLOSd trial has ended. Please contact your account admin to set up billing.")}
        </p>
        {isAdmin() && !isMobileNative && (
          <button
            onClick={onSetUpBilling}
            style={{
              backgroundColor: '#75BB2E', color: '#fff', border: 'none',
              borderRadius: 8, padding: '13px 32px', fontSize: 15,
              fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>
            Set Up Billing →
          </button>
        )}
        {isMobileNative && (
          <button
            onClick={handleLogout}
            style={{
              backgroundColor: '#75BB2E', color: '#fff', border: 'none',
              borderRadius: 8, padding: '13px 32px', fontSize: 15,
              fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>
            Got it
          </button>
        )}
        {!isMobileNative && (
          <div style={{ marginTop: 20 }}>
            <button onClick={handleLogout} style={{
              background: 'none', border: 'none',
              color: '#89A8B1', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Log out
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // --- Impersonation Banner (fixed position — shows on ALL views) ---
  const ImpersonationBanner = () => {
    if (!impersonation) return null;
    // On native, push below status bar via safe-area-inset-top.
    const nativeTop = isMobileNative
      ? { top: 'env(safe-area-inset-top, 0px)' }
      : {};
    return (
      <div
        className="fixed top-0 left-0 right-0 flex items-center justify-between px-4 py-2"
        style={{ backgroundColor: '#E53E3E', color: '#FFFFFF', zIndex: 9999, ...nativeTop }}
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Eye className="w-4 h-4" />
          Viewing as {impersonation.memberName} — {impersonation.orgName}
        </div>
        <button
          onClick={handleExitImpersonation}
          className="px-3 py-1 rounded text-sm font-semibold"
          style={{ backgroundColor: '#FFFFFF', color: '#E53E3E' }}
        >
          Exit Impersonation
        </button>
      </div>
    );
  };

  // Padding to push content below the fixed banner
  const isMobile = window.innerWidth < 768;

  // Mobile bottom nav bar
  const MobileFooterNav = () => {
    if (!isMobile) return null;
    return (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        backgroundColor: '#071D39',
        display: 'flex', justifyContent: 'space-around', alignItems: 'center',
        padding: '8px 0', paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
        zIndex: 9998, borderTop: '2px solid #75BB2E'
      }}>
        {[
          { icon: <LayoutGrid size={20} />, label: 'Dashboard', view: 'pipeline' },
          { icon: <CheckSquare size={20} />, label: 'Tasks', view: 'dashboard' },
          { icon: <PlusCircle size={20} />, label: 'New Deal', action: () => setShowNewProjectModal(true) },
          { icon: <Settings size={20} />, label: 'Settings', view: 'teamSettings' },
        ].map((item, i) => (
          <button
            key={i}
            onClick={() => item.action ? item.action() : setCurrentView(item.view)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: currentView === item.view ? '#75BB2E' : 'rgba(255,255,255,0.6)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '2px', padding: '4px 12px', fontSize: '10px', fontWeight: 600,
            }}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    );
  };
  const totalBannerHeight = (impersonation ? 40 : 0) + (showTrialBanner ? 40 : 0);
  // On native, the banners are pushed down by safe-area-inset-top, so the
  // content needs to reserve that AS WELL as the banner heights.
  const impersonationPadding = totalBannerHeight > 0
    ? { paddingTop: isMobileNative
        ? `calc(${totalBannerHeight}px + env(safe-area-inset-top, 0px))`
        : `${totalBannerHeight}px` }
    : {};
  // Mobile content padding: clear the iOS notch on native, clear the home
  // indicator at the bottom, and give every view a bit of breathing room.
  const mobilePadding = isMobile ? {
    paddingTop: isMobileNative
      ? 'calc(env(safe-area-inset-top, 0px) + 12px)'
      : '50px',
    paddingBottom: isMobileNative
      ? 'calc(env(safe-area-inset-bottom, 0px) + 80px)'
      : '80px',
  } : {};

  if (trialExpired) return (
    <>
      <TrialExpiredScreen onSetUpBilling={() => { if (!isMobileNative) setShowBillingFromPaywall(true); }} />
      {showBillingFromPaywall && !isMobileNative && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setShowBillingFromPaywall(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#89A8B1' }}>✕</button>
            <BillingTab orgId={orgId} user={user} teamMembers={teamMembers} orgPlan={orgPlan} hideDescription={true} />
          </div>
        </div>
      )}
    </>
  );

  if (currentView === 'helpSupport') {
    return (
      <>
        <ImpersonationBanner />
        <TrialBanner />
        <div className="flex h-screen" style={{ backgroundColor: '#FFFFFF', ...impersonationPadding }}>
          <Sidebar {...sidebarProps} />
          <div className="flex-1 overflow-auto" style={mobilePadding}>
            <SupportPage user={user} orgId={orgId} />
          </div>
        </div>
        {showNewProjectModal && (
          <NewProjectModal
            onClose={() => setShowNewProjectModal(false)}
            onSave={createProject}
            teamMembers={teamMembers}
          />
        )}
        <MobileFooterNav />
      </>
    );
  }
  if (currentView === 'myProfile') {
    const currentMember = getCurrentUserMember();
    if (!currentMember) {
      setCurrentView('pipeline');
      return null;
    }
    return (
        <>
          <ImpersonationBanner />
          <div className="flex h-screen" style={{ backgroundColor: '#FFFFFF', ...impersonationPadding }}>
            <Sidebar {...sidebarProps} />
          <div className="flex-1 overflow-auto" style={mobilePadding}>
            <MyProfilePage
              member={currentMember}
              isAdmin={isAdmin()}
              onSave={(memberId, updates) => {
                updateTeamMember(memberId, updates);
              }}
            />
          </div>
          </div>
        <MobileFooterNav />
        </>
      );
  }
  if (currentView === 'superAdmin' && isSuperAdmin()) {
    return (
      <div className="flex h-screen" style={{ backgroundColor: '#FFFFFF' }}>
        <Sidebar {...sidebarProps} />
        <div className="flex-1 overflow-auto" style={mobilePadding}>
          <SuperAdminDashboard onImpersonate={handleImpersonate} />
        </div>
        {showNewProjectModal && (
          <NewProjectModal
            onClose={() => setShowNewProjectModal(false)}
            onSave={createProject}
            teamMembers={teamMembers}
          />
        )}
        <MobileFooterNav />
      </div>
    );
  }

  if (currentView === 'pipeline') {
    return (
      <>
        <ImpersonationBanner />
        <TrialBanner />
        <div className="flex h-screen" style={{ backgroundColor: '#FFFFFF', ...impersonationPadding }}>

          <Sidebar {...sidebarProps} />
          <div className="flex-1 overflow-auto" style={mobilePadding}>
            <SalesPipeline
              projects={activeProjects}
              getDealTitle={getDealTitle}
            orgId={orgId}
                orgName={orgName}
                closedDeals={projects.filter(p => p.status === 'closed')}
              onSelectProject={(project) => {
                if (project) handleSelectProject(project);
              }}
            />
          </div>
          {showNewProjectModal && (
            <NewProjectModal
              onClose={() => setShowNewProjectModal(false)}
              onSave={createProject}
              teamMembers={teamMembers}
            />
          )}
        </div>
        <MobileFooterNav />
      </>
    );
  }

  if (currentView === 'dashboard') {
    return (
      <>
        <ImpersonationBanner />
        <TrialBanner />
        <div style={{...impersonationPadding, ...mobilePadding}}>
          <DashboardView
        user={user}
        activeProjects={activeProjects}
        teamMembers={teamMembers}
        currentView={currentView}
        {...sidebarProps}
        getTaskColor={getTaskColor}
        isTaskUrgent={isTaskUrgent}
        getMyTasks={getMyTasks}
        getDueSoonTasks={getDueSoonTasks}
        getOverdueTasks={getOverdueTasks}
        getMyTasksGroupedByDeal={getMyTasksGroupedByDeal}
        getDueSoonTasksGroupedByDeal={getDueSoonTasksGroupedByDeal}
        getOverdueTasksGroupedByDeal={getOverdueTasksGroupedByDeal}
        showNewProjectModal={showNewProjectModal}
        onCloseNewProject={() => setShowNewProjectModal(false)}
        createProject={createProject}
        showTeamModal={showTeamModal}
        onCloseTeamModal={() => setShowTeamModal(false)}
        addTeamMember={addTeamMember}
        removeTeamMember={removeTeamMember}
        onEditTeamMember={handleEditTeamMember}
        showEditProfileModal={showEditProfileModal}
        editProfileMember={editProfileMember}
        editProfileMode={editProfileMode}
        onCloseEditProfile={handleCloseEditProfile}
        onSaveEditProfile={handleSaveEditProfile}
        showMyTasksModal={showMyTasksModal}
        myTasksModalDeal={myTasksModalDeal}
        myTasksModalType={myTasksModalType}
        onCloseMyTasksModal={handleCloseMyTasksModal}
        onMyTasksDealClick={handleMyTasksDealClick}
        onMyTasksTaskClick={handleMyTasksTaskClick}
        showTaskDetailModal={showTaskDetailModal}
        taskDetailTask={taskDetailTask}
        taskDetailProject={taskDetailProject}
        onCloseTaskDetail={handleCloseTaskDetail}
        onTaskDetailUpdate={handleTaskDetailUpdate}
      />
        </div>
        <MobileFooterNav />
      </>
    );
  }

  if (currentView === 'teamSettings') {
    return (
      <>
        <ImpersonationBanner />
        <TrialBanner />
        <div className="flex h-screen" style={{ backgroundColor: '#FFFFFF', ...impersonationPadding }}>
          <Sidebar {...sidebarProps} />
          <div className="flex-1 overflow-auto" style={mobilePadding}>
            <SettingsPage
              teamMembers={teamMembers}
              onAddMember={addTeamMember}
              onRemoveMember={removeTeamMember}
              onEditMember={handleEditTeamMember}
              defaultAssignments={defaultAssignments}
              onSaveDefaults={handleSaveDefaultAssignments}
              sellerTemplate={SELLER_TEMPLATE}
              buyerTemplate={BUYER_TEMPLATE}
              isAdmin={isAdmin()}
              projects={projects}
              orgId={orgId}
              user={user}
              currentMember={getCurrentUserMember()}
              onSave={(memberId, updates) => updateTeamMember(memberId, updates)}
              orgPlan={orgPlan}
onRemoveCollaborator={async (dealId, userId) => {

                const { removeCollaborator } = await import('./services/firestoreService');
                await removeCollaborator(orgId, dealId, userId);
                setProjects(prev => prev.map(p =>
                  p.id === dealId
                    ? { ...p, collaborators: (p.collaborators || []).filter(c => c.userId !== userId) }
                    : p
                ));
              }}
            />
          </div>
          {showEditProfileModal && editProfileMember && (
            <EditProfileModal
              member={editProfileMember}
              mode={editProfileMode}
              isAdmin={isAdmin()}
              onClose={handleCloseEditProfile}
              onSave={handleSaveEditProfile}
            />
          )}
          {showNewProjectModal && (
            <NewProjectModal
              onClose={() => setShowNewProjectModal(false)}
              onSave={createProject}
              teamMembers={teamMembers}
            />
          )}
        </div>
        <MobileFooterNav />
      </>
    );
  }

  if (currentView === 'archive') {
    return (
      <>
        <ImpersonationBanner />
        <TrialBanner />
        <div style={{...impersonationPadding, ...mobilePadding}}>

          <ArchiveView
            activeProjects={activeProjects}
            archivedProjects={archivedProjects}
            teamMembers={teamMembers}
            currentView={currentView}
            {...sidebarProps}
            unarchiveProject={unarchiveProject}
            onDeleteDeal={handleDeleteDeal}
            showNewProjectModal={showNewProjectModal}
            onCloseNewProject={() => setShowNewProjectModal(false)}
            createProject={createProject}
            showTeamModal={showTeamModal}
            onCloseTeamModal={() => setShowTeamModal(false)}
            addTeamMember={addTeamMember}
            removeTeamMember={removeTeamMember}
            onEditTeamMember={handleEditTeamMember}
            showDeleteModal={showDeleteModal}
            dealToDelete={dealToDelete}
            onConfirmDelete={handleConfirmDelete}
            onCancelDelete={handleCancelDelete}
            showEditProfileModal={showEditProfileModal}
            editProfileMember={editProfileMember}
            editProfileMode={editProfileMode}
            onCloseEditProfile={handleCloseEditProfile}
            onSaveEditProfile={handleSaveEditProfile}
          />
        </div>
        <MobileFooterNav />
      </>
    );
  }

  // Project detail view
  return (
    <>
      <ImpersonationBanner />
      <div style={{...impersonationPadding, ...mobilePadding}}>
        <ProjectView
            onTasksReloaded={reloadProjectTasks}
      activeProjects={activeProjects}
      selectedProject={selectedProject}
      selectedTask={selectedTask}
      teamMembers={teamMembers}
      currentView={currentView}
      {...sidebarProps}
      getTaskColor={getTaskColor}
      isTaskUrgent={isTaskUrgent}
      onTaskClick={setSelectedTask}
      updateProject={updateProject}
      onCloseTask={() => setSelectedTask(null)}
      onUpdateTask={(updates) =>
        updateTask(selectedProject.id, selectedTask.id, updates)
      }
      onUpdateTaskById={(projectId, taskId, updates) =>
        updateTask(projectId, taskId, updates)
      }
      updateTasksBatch={updateTasksBatch}
      showNewProjectModal={showNewProjectModal}
      onCloseNewProject={() => setShowNewProjectModal(false)}
      createProject={createProject}
      showTeamModal={showTeamModal}
      onCloseTeamModal={() => setShowTeamModal(false)}
      addTeamMember={addTeamMember}
      removeTeamMember={removeTeamMember}
      onEditTeamMember={handleEditTeamMember}
      showEditProfileModal={showEditProfileModal}
      editProfileMember={editProfileMember}
      editProfileMode={editProfileMode}
      onCloseEditProfile={handleCloseEditProfile}
      onSaveEditProfile={handleSaveEditProfile}
      showArchiveModal={showArchiveModal}
      onOpenArchiveModal={() => setShowArchiveModal(true)}
      onArchiveProject={archiveProject}
      onCloseArchiveModal={() => setShowArchiveModal(false)}
      showDeleteModal={showDeleteModal}
      dealToDelete={dealToDelete}
      onDeleteDeal={handleDeleteDeal}
      onConfirmDelete={handleConfirmDelete}
      onCancelDelete={handleCancelDelete}
      showEditProjectModal={showEditProjectModal}
      onOpenEditProject={() => setShowEditProjectModal(true)}
      onCloseEditProject={() => setShowEditProjectModal(false)}
      onSaveEditProject={(updates) => {
        updateProject(selectedProject.id, updates);
        setShowEditProjectModal(false);
      }}
    />
      </div>
      <MobileFooterNav />
    </>
  );
};

export default App;
